import { lazy, Suspense } from 'react';
import Typography from '@mui/material/Typography';
import { useOutletContext } from 'react-router';
import type { PlantDetailQuery } from '../../../gql/graphql';
import { displayText } from '../../../components/ui';

const LocationMap = lazy(() =>
  import('../../../components/ui/LocationMap').then((module) => ({ default: module.LocationMap })),
);

type PlantDetail = NonNullable<PlantDetailQuery['plantDetail']>;

export function PlantGeneralTab() {
  const plant = useOutletContext<PlantDetail>();
  const longitude = plant.longitude == null ? 0 : Number(plant.longitude);
  const latitude = plant.latitude == null ? 0 : Number(plant.latitude);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-6" data-testid="plant-general-tab">
      <div className="flex flex-col gap-6">
        <Section title="General Information">
          <FieldRow label="Plant Name" value={plant.plantName} />
          <FieldRow label="Plant Code" value={plant.plantCode} />
          <FieldRow label="Management Code" value={plant.managementCode} />
          <FieldRow label="Area" value={plant.area} />
          <FieldRow label="Country" value={plant.country} />
          <FieldRow label="Region" value={plant.region} />
          <FieldRow label="Business Line" value={plant.businessLine} />
          <FieldRow label="Operating Company" value={plant.operatingCompany} />
        </Section>
        <Section title="Location Details">
          <FieldRow label="City" value={plant.city} />
          <FieldRow label="Zip Code" value={plant.zipCode} />
          <FieldRow label="Time Zone" value={plant.windowsTimeZone} />
        </Section>
        {(plant.areaFleetManagers.length > 0 || plant.countryFleetManagers.length > 0 || plant.pointsOfContact.length > 0) && (
          <Section title="Fleet Responsibilities">
            {plant.areaFleetManagers.length > 0 && <NameList title="Area Fleet Managers" names={plant.areaFleetManagers} />}
            {plant.countryFleetManagers.length > 0 && <NameList title="Country Fleet Managers" names={plant.countryFleetManagers} />}
            {plant.pointsOfContact.length > 0 && <NameList title="Points of Contact" names={plant.pointsOfContact} />}
          </Section>
        )}
      </div>
      <Suspense fallback={<div className="min-h-[300px]" />}>
        <LocationMap lat={latitude} lng={longitude} label={plant.plantName} zoom={10} />
      </Suspense>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <Typography variant="overline" color="text.secondary" component="h2" sx={{ display: 'block', mb: 1 }}>
        {title}
      </Typography>
      <div className="flex flex-col gap-1">{children}</div>
    </section>
  );
}

function FieldRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="grid grid-cols-[1fr_2fr] gap-2 py-1 border-b border-divider last:border-0">
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2">{displayText(value)}</Typography>
    </div>
  );
}

function NameList({ title, names }: { title: string; names: readonly string[] }) {
  return (
    <div className="py-1">
      <Typography variant="body2" color="text.secondary">{title}</Typography>
      <Typography variant="body2">{names.join(', ')}</Typography>
    </div>
  );
}